import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import NumberFormat from "react-number-format";

import * as yup from "yup";
import UndoIcon from "@mui/icons-material/Undo";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { IconButton, Stack, TextField } from "@mui/material";
import { BinDeleteIcon } from "assets/material-custom-icons";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useModals } from "hooks";
import { debounce } from "lodash";
import { ICharContact, ICharContactFieldVisibility } from "store/types";

interface Props {
  contact: ICharContact;
  onChange: (id: number | string, key: keyof ICharContact, value: string) => void;
  onVisibility: (id: number | string, key: keyof ICharContactFieldVisibility) => void;
  disabled?: boolean;
  deleteContact: (id: number | string) => void;
}

export const Contact: React.FC<Props> = ({ contact, onChange, onVisibility, disabled, deleteContact }) => {
  const { confirmModal } = useModals();
  const [isValidEmail, setIsValidEmail] = useState({ isValid: false, error: "" });
  const isDeleted = useMemo(() => contact.is_deleted, [contact]);

  const emailValidationSchema = yup.object().shape({
    email: yup.string().email("Invalid email address")
  });

  const pendingRef = useRef<Partial<Record<keyof ICharContact, string>>>({});

  const flushPending = useCallback(() => {
    if (!contact.id) return;
    const pending = pendingRef.current;
    pendingRef.current = {};
    for (const [key, value] of Object.entries(pending)) {
      onChange(contact.id, key as keyof ICharContact, value as string);
    }
  }, [contact.id, onChange]);

  const flushPendingDebounced = useMemo(() => debounce(flushPending, 500), [flushPending]);

  useEffect(
    () => () => {
      flushPendingDebounced.flush();
    },
    [flushPendingDebounced]
  );

  const onChangeDebounce = useCallback(
    (id: number | string | undefined, key: keyof ICharContact, value: string) => {
      if (!id) return;
      pendingRef.current[key] = value;
      flushPendingDebounced();
    },
    [flushPendingDebounced]
  );

  if (!contact) return null;

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    emailValidationSchema
      .validate({ email })
      .then(() => {
        setIsValidEmail({ isValid: true, error: "" });
        onChangeDebounce(contact.id, "email", e.target.value);
      })
      .catch((error) => {
        setIsValidEmail({ isValid: false, error: error.errors[0] });
      });
  };

  const handleDelete = (id: number | string) => {
    if (isDeleted) {
      deleteContact(id);
      return;
    }
    confirmModal({
      msg: `Are you sure you want to remove the contact "${contact.first_name} ${contact.last_name}"?`,
      title: "Remove Contact",
      action: () => deleteContact(id)
    });
  };

  return (
    <Stack alignItems="center" gap="20px">
      <Stack direction="row" sx={{ width: "100%" }} alignItems="center">
        <TextField
          fullWidth
          label="First Name"
          defaultValue={contact.first_name}
          onChange={(e) => onChangeDebounce(contact.id, "first_name", e.target.value)}
          disabled={disabled || isDeleted}
          {...(isDeleted && { sx: { input: { textDecoration: "line-through" } } })}
        />
        {!disabled && (
          <Stack onClick={() => contact.id && handleDelete(contact.id)} sx={{ marginLeft: "10px" }}>
            <MaterialTooltip isShow title="Delete contact">
              <IconButton>{contact.is_deleted ? <UndoIcon /> : <BinDeleteIcon />}</IconButton>
            </MaterialTooltip>
          </Stack>
        )}
        {!disabled && (
          <Stack onClick={() => contact.id && onVisibility(contact.id, "first_name")} sx={{ marginLeft: "10px" }}>
            <IconButton>
              {contact.is_show.first_name ? (
                <VisibilityOutlinedIcon />
              ) : (
                <VisibilityOffOutlinedIcon sx={{ color: "#92A7BC" }} />
              )}
            </IconButton>
          </Stack>
        )}
      </Stack>

      <Stack direction="row" sx={{ width: "100%" }} alignItems="center">
        <TextField
          fullWidth
          label="Last Name"
          defaultValue={contact.last_name}
          onChange={(e) => onChangeDebounce(contact.id, "last_name", e.target.value)}
          disabled={disabled || isDeleted}
          {...(isDeleted && { sx: { input: { textDecoration: "line-through" } } })}
        />
        {!disabled && (
          <Stack onClick={() => contact.id && onVisibility(contact.id, "last_name")} sx={{ marginLeft: "10px" }}>
            <IconButton disabled={isDeleted}>
              {contact.is_show.last_name ? (
                <VisibilityOutlinedIcon />
              ) : (
                <VisibilityOffOutlinedIcon sx={{ color: "#92A7BC" }} />
              )}
            </IconButton>
          </Stack>
        )}
      </Stack>
      <Stack direction="row" sx={{ width: "100%" }} alignItems="center">
        <NumberFormat
          isNumericString
          fullWidth
          label="Phone Number"
          customInput={TextField}
          defaultValue={contact.phone_number}
          onValueChange={({ value }) => onChangeDebounce(contact.id, "phone_number", value)}
          disabled={disabled || isDeleted}
          {...(isDeleted && { sx: { input: { textDecoration: "line-through" } } })}
        />
        {!disabled && (
          <Stack onClick={() => contact.id && onVisibility(contact.id, "phone_number")} sx={{ marginLeft: "10px" }}>
            <IconButton disabled={isDeleted}>
              {contact.is_show.phone_number ? (
                <VisibilityOutlinedIcon />
              ) : (
                <VisibilityOffOutlinedIcon sx={{ color: "#92A7BC" }} />
              )}
            </IconButton>
          </Stack>
        )}
      </Stack>

      <Stack direction="row" sx={{ width: "100%" }} alignItems="center">
        <TextField
          sx={{
            ".MuiFormHelperText-root": {
              bottom: "-17px",
              position: "absolute",
              color: "red !important",
              fontSize: "11px",
              marginLeft: "0px"
            }
          }}
          fullWidth
          label="Email"
          defaultValue={contact.email}
          onChange={handleEmailChange}
          helperText={isValidEmail.error}
          error={!!isValidEmail.error}
          disabled={disabled || isDeleted}
          {...(isDeleted && { sx: { input: { textDecoration: "line-through" } } })}
        />
        {!disabled && (
          <Stack onClick={() => contact.id && onVisibility(contact.id, "email")} sx={{ marginLeft: "10px" }}>
            <IconButton disabled={isDeleted}>
              {contact.is_show.email ? (
                <VisibilityOutlinedIcon />
              ) : (
                <VisibilityOffOutlinedIcon sx={{ color: "#92A7BC" }} />
              )}
            </IconButton>
          </Stack>
        )}
      </Stack>

      <Stack direction="row" sx={{ width: "100%" }} alignItems="center">
        <TextField
          fullWidth
          label="Title"
          defaultValue={contact.title}
          onChange={(e) => onChangeDebounce(contact.id, "title", e.target.value)}
          disabled={disabled || isDeleted}
          {...(isDeleted && { sx: { input: { textDecoration: "line-through" } } })}
        />
        {!disabled && (
          <Stack onClick={() => contact.id && onVisibility(contact.id, "title")} sx={{ marginLeft: "10px" }}>
            <IconButton disabled={isDeleted}>
              {contact.is_show.title ? (
                <VisibilityOutlinedIcon />
              ) : (
                <VisibilityOffOutlinedIcon sx={{ color: "#92A7BC" }} />
              )}
            </IconButton>
          </Stack>
        )}
      </Stack>
      <Stack direction="row" sx={{ width: "100%" }} alignItems="center">
        <TextField
          fullWidth
          label="Firm"
          defaultValue={contact.firm}
          onChange={(e) => onChangeDebounce(contact.id, "firm", e.target.value)}
          disabled={disabled || isDeleted}
          {...(isDeleted && { sx: { input: { textDecoration: "line-through" } } })}
        />
        {!disabled && (
          <Stack onClick={() => contact.id && onVisibility(contact.id, "firm")} sx={{ marginLeft: "10px" }}>
            <IconButton disabled={isDeleted}>
              {contact.is_show.firm ? (
                <VisibilityOutlinedIcon />
              ) : (
                <VisibilityOffOutlinedIcon sx={{ color: "#92A7BC" }} />
              )}
            </IconButton>
          </Stack>
        )}
      </Stack>

      <Stack direction="row" sx={{ width: "100%" }} alignItems="center">
        <TextField
          fullWidth
          label="Address"
          defaultValue={contact.address}
          onChange={(e) => onChangeDebounce(contact.id, "address", e.target.value)}
          disabled={disabled || isDeleted}
          {...(isDeleted && { sx: { input: { textDecoration: "line-through" } } })}
        />
        {!disabled && (
          <Stack onClick={() => contact.id && onVisibility(contact.id, "address")} sx={{ marginLeft: "10px" }}>
            <IconButton disabled={isDeleted}>
              {contact.is_show.address ? (
                <VisibilityOutlinedIcon />
              ) : (
                <VisibilityOffOutlinedIcon sx={{ color: "#92A7BC" }} />
              )}
            </IconButton>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
